/* @flow */

import {
  FloatingArrow,
  FloatingFocusManager,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  safePolygon,
  shift,
  size,
  useClick,
  useDelayGroup,
  useDismiss,
  useFloating,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import clsx from 'clsx';
import * as React from 'react';

import { logError } from '../../helpers/debug/debug';
import { TOOLTIP } from '../../helpers/ui/constants';
import './Tooltip.css';

export type TooltipPlacement = 'top' | 'top-start' | 'top-end' | 'right' | 'right-start' | 'right-end' | 'bottom' | 'bottom-start' | 'bottom-end' | 'left' | 'left-start' | 'left-end';

export type TooltipStrategy = 'absolute' | 'fixed';

export type TooltipPropType = {
  +arrowClassName?: string,
  +children: React.Node,
  +className?: string,
  +closeDelay?: number,
  +content: React.Node | null,
  +disabled?: boolean,
  +interactive?: boolean,
  +maxWidth?: number,
  +offsetValue?: number,
  +onOpenChange?: (open: boolean) => void,
  +open?: boolean,
  +openDelay?: number,
  +placement?: TooltipPlacement,
  +showArrow?: boolean,
  +strategy?: TooltipStrategy,
  +zIndex?: number,
};

// eslint-disable-next-line max-lines-per-function
const Tooltip = (props: TooltipPropType, forwardedRef: React.ElementRef<'div'> | null) => {
  const {
    arrowClassName = '',
    children,
    className = '',
    closeDelay = TOOLTIP.DefaultCloseDelay,
    content,
    disabled = false,
    interactive = false,
    maxWidth = TOOLTIP.DefaultMaxWidth,
    offsetValue = TOOLTIP.DefaultOffsetValue,
    onOpenChange,
    open: controlledOpen,
    openDelay = TOOLTIP.DefaultOpenDelay,
    placement = 'bottom',
    showArrow = false,
    strategy = 'absolute',
    zIndex = TOOLTIP.DefaultZIndex,
  } = props;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);
  const [activeModal, setActiveModal] = React.useState<HTMLElement | null>(null);

  const open = controlledOpen !== undefined ? controlledOpen : uncontrolledOpen;

  const setOpen = React.useCallback(
    (nextOpen: boolean) => {
      if (onOpenChange) {
        onOpenChange(nextOpen);
      }

      setUncontrolledOpen(nextOpen);
    },
    [onOpenChange],
  );

  const arrowRef = React.useRef(null);

  const tooltipId = React.useId();

  React.useEffect(() => {
    const updateActiveModal = () => {
      // Find the open modal dialog (or highest top-layer modal)
      const openDialogs = document.querySelectorAll<HTMLElement>('dialog[open]');
      const topModal = openDialogs.length > 0 ? openDialogs[openDialogs.length - 1] : null;
      setActiveModal(topModal);
    };

    updateActiveModal();

    const MutationObserverClass = window.MutationObserver;
    if (!MutationObserverClass) {
      return;
    }

    const observer = new MutationObserverClass(updateActiveModal);
    const targetElement = document.body || document.documentElement;

    if (targetElement) {
      observer.observe(targetElement, {
        attributeFilter: ['open'],
        attributes: true,
        childList: true,
        subtree: true,
      });
    }

    return () => observer.disconnect();
  }, []);

  const { context, floatingStyles, refs } = useFloating({
    middleware: [
      offset(offsetValue),

      flip({
        padding: 8,
      }),

      shift({
        padding: 8,
      }),

      size({
        apply({ availableWidth, elements }) {
          elements.floating.style.maxWidth = `${Math.min(maxWidth, availableWidth)}px`;
        },
      }),

      showArrow
        ? arrow({
            element: arrowRef,
          })
        : null,
    ].filter(Boolean),

    onOpenChange: setOpen,
    open,
    placement,
    strategy,
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    delay: {
      close: closeDelay,
      open: openDelay,
    },
    enabled: !disabled,
    handleClose: interactive ? safePolygon() : undefined,
    move: false,
  });

  useDelayGroup(context, { id: tooltipId });

  const click = useClick(context, { enabled: !disabled });

  // Prevent from bubbling to any parent dialog and closing it as well
  const dismiss = useDismiss(context, {
    bubbles: {
      escape: false,
    },
  });

  // When interactive is true, switch ARIA role to dialog for screen reader compatibility
  const role = useRole(context, { role: interactive ? 'dialog' : 'tooltip' });

  const interactions = useInteractions([hover, click, dismiss, role]);

  // Intercept Escape key globally in capture phase to stop native <dialog> cancel behavior
  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleGlobalKeyDown = (event: SyntheticKeyboardEvent<HTMLElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        event.stopImmediatePropagation();
        event.preventDefault();
        setOpen(false);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown, true);

    return () => {
      window.removeEventListener('keydown', handleGlobalKeyDown, true);
    };
  }, [open, setOpen]);

  if (!React.isValidElement(children)) {
    logError('[Tooltip] children must be a valid React element.');
    return null;
  }

  const child = children as React.ElementRef<'div'> | React.ElementRef<'span'>;
  const childRef = child.ref;

  const mergedRef = useMergeRefs([refs.setReference, forwardedRef, childRef]);

  const trigger = React.cloneElement(
    child,
    interactions.getReferenceProps({
      ref: mergedRef,
      ...child.props,
    }),
  );

  const shouldRenderTooltip = !disabled && open === true && content !== null;

  /* eslint-disable react/jsx-props-no-spreading */
  return (
    <>
      {trigger}

      {shouldRenderTooltip ? (
        <FloatingPortal root={activeModal ?? undefined}>
          <FloatingFocusManager context={context} disabled={!interactive} modal={interactive} returnFocus>
            <div
              ref={refs.setFloating}
              style={{
                ...floatingStyles,
                zIndex,
              }}
              {...interactions.getFloatingProps({ className: clsx('tooltip', className) })}
            >
              {content}

              {showArrow ? <FloatingArrow className={arrowClassName} context={context} fill='black' ref={arrowRef} /> : null}
            </div>
          </FloatingFocusManager>
        </FloatingPortal>
      ) : null}
    </>
  );
  /* eslint-enable react/jsx-props-no-spreading */
};

export default React.forwardRef(Tooltip) as React.ComponentType<TooltipPropType>;
